/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';

import type {
	AccessRole,
	ViewAccessLevel,
} from '@jira/polaris__domain-view/src/ViewAccessLevel.types.tsx';
import {
	ACCESS_ROLE_EDITOR,
	ACCESS_ROLE_VIEWER,
} from '@jira/polaris__domain-view/src/viewAccessLevels.constants.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import { isAccessForbiddenError } from '@jira/polaris__lib-errors/src/isPermissionError.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { experience } from '@jira/polaris__lib-analytics/src/ConstantsExperience.tsx';
import { createErrorAnalytics } from '@jira/polaris__lib-errors/src/createErrorAnalytics.tsx';
import type { State, Props } from './State.types.tsx';
import { fetchViewPermissionsForCurrentUser } from './fetchPermissions.tsx';

const DEFAULT_CACHE_TTL = 30_000; // 30 seconds

export type LoadViewPermissionsForCurrentUserState =
	| 'succeeded'
	| 'loading'
	| 'no-access'
	| 'failed';

/**
 * Load view permissions for the current user and store them in the state.
 * If the permissions are already cached and not expired, do nothing.
 * If the permissions are being loaded, do nothing.
 * @param containerId - view UUID or collection container ID
 * @returns The access role, access level and the state of the loading process.
 */
export const loadViewPermissionsForCurrentUser =
	(
		containerId: string,
	): Action<
		State,
		Props,
		Promise<{
			role: AccessRole | undefined;
			accessLevel: ViewAccessLevel | undefined;
			state: LoadViewPermissionsForCurrentUserState;
		}>
	> =>
	async ({ getState, setState }, props) => {
		const cacheTTL = props.cacheTTL ?? DEFAULT_CACHE_TTL;
		const cachedViewPermissions = getState().viewPermissions[containerId];
		const isCached = cachedViewPermissions?.loadedTimestamp !== undefined;
		const isExpired =
			isCached &&
			performance.now() - (getState().viewPermissions[containerId].loadedTimestamp || 0) > cacheTTL;

		if ((cachedViewPermissions && isCached && !isExpired) || cachedViewPermissions?.loading) {
			let role: AccessRole | undefined;
			if (cachedViewPermissions.data?.canEdit) {
				role = ACCESS_ROLE_EDITOR;
			} else if (cachedViewPermissions.data?.canView) {
				role = ACCESS_ROLE_VIEWER;
			}

			return {
				role,
				accessLevel: cachedViewPermissions.data?.accessLevel,
				state: cachedViewPermissions?.loading ? 'loading' : 'succeeded',
			};
		}

		setState({
			viewPermissions: {
				...getState().viewPermissions,
				[containerId]: {
					...getState().viewPermissions[containerId],
					loading: true,
				},
			},
		});

		experience.viewPermissions.loadCurrentUserViewPermissions.start();

		try {
			const { canView, canEdit, accessLevel } =
				await fetchViewPermissionsForCurrentUser(containerId);

			experience.viewPermissions.loadCurrentUserViewPermissions.success();

			setState({
				viewPermissions: {
					...getState().viewPermissions,
					[containerId]: {
						loading: false,
						loadedTimestamp: performance.now(),
						data: {
							canView,
							canEdit,
							accessLevel,
						},
					},
				},
			});

			return {
				role: canEdit ? ACCESS_ROLE_EDITOR : ACCESS_ROLE_VIEWER,
				accessLevel,
				state: 'succeeded',
			};

			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			if (isAccessForbiddenError(error)) {
				experience.viewPermissions.loadCurrentUserViewPermissions.abort(error);

				setState({
					viewPermissions: {
						...getState().viewPermissions,
						[containerId]: {
							...getState().viewPermissions[containerId],
							data: undefined,
							loading: false,
						},
					},
				});

				return {
					role: undefined,
					accessLevel: undefined,
					state: 'no-access',
				};
			}

			if (isClientFetchError(error)) {
				experience.viewPermissions.loadCurrentUserViewPermissions.abort(error);
			} else {
				experience.viewPermissions.loadCurrentUserViewPermissions.failure(error);
			}

			fireErrorAnalytics(
				createErrorAnalytics(
					'polaris.permissions-store.load-view-permissions-for-current-user',
					error,
				),
			);

			setState({
				viewPermissions: {
					...getState().viewPermissions,
					[containerId]: {
						...getState().viewPermissions[containerId],
						loading: false,
					},
				},
			});

			return {
				role: undefined,
				accessLevel: undefined,
				state: 'failed',
			};
		}
	};
